import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { getCurrentSeason } from "@/config/hours";
import { useBookNow } from "@/hooks/useBookNow";

// Import your local logo image (commented out for now)
// import logoImage from "@/assets/images/logo_6.jpg";

// External URL (commented out)
// const logoImage = "https://karting-bg.com/assets/images/logo_6.jpg";

// Placeholder - using text instead of logo
const logoText = "KARTING KRANSA POLYANA";

export const Footer = () => {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  const bookNow = useBookNow();
  const season = getCurrentSeason();
  const hours = season.hours.map((h) => ({
    day: lang === "en" ? h.dayEn : h.dayBg,
    time: h.time,
  }));
  const seasonLabel = lang === "en" ? season.labelEn : season.labelBg;

  return (
    <footer className="relative bg-[#050505] text-white border-t border-white/10" data-testid="footer">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand & contact */}
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo">
              {/* Logo image - commented out */}
              {/* <img
                src={logoImage}
                alt="Karting Track Sofia"
                className="h-10 md:h-12 w-auto"
                style={{ mixBlendMode: "screen" }}
              /> */}
              
              {/* Text placeholder instead of logo */}
              <span className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tighter text-white">
                {logoText}
              </span>
              
              {/* Optional: small decorative dot */}
              <span className="w-2 h-2 bg-[#FF2D2D] rounded-full" />
            </Link>
            <p className="mt-4 text-sm text-white/50 max-w-sm leading-relaxed">
              {t.footer.tagline}. 960m · 16 turns · 70 km/h.
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <a href="tel:+359888860950" className="flex items-center gap-3 text-white/70 hover:text-[#FF2D2D] transition-colors" data-testid="footer-phone">
                <Phone size={14} className="text-[#FF2D2D]" /> +359 88 8860950
              </a>
              <a href="mailto:kartingsport@abv.bg" className="flex items-center gap-3 text-white/70 hover:text-[#FF2D2D] transition-colors" data-testid="footer-email">
                <Mail size={14} className="text-[#FF2D2D]" /> kartingsport@abv.bg
              </a>
              <a
                href="https://www.google.com/maps/place/%D0%9A%D0%B0%D1%80%D1%82%D0%B8%D0%BD%D0%B3+%E2%80%9E%D0%9A%D1%80%D0%B0%D1%81%D0%BD%D0%B0+%D0%BF%D0%BE%D0%BB%D1%8F%D0%BD%D0%B0%E2%80%9C/@42.6916936,23.2866934,17z"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/70 hover:text-[#FF2D2D] transition-colors"
                data-testid="footer-address"
              >
                <MapPin size={14} className="text-[#FF2D2D]" /> {t.contact.address}
              </a>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.facebook.com/share/198iyukZhA/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#FF2D2D] hover:text-[#FF2D2D] transition-colors"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook size={14} />
              </a>
              <a
                href="https://www.instagram.com/karting_krasna_polyana?stkn=MWRsdm1lNmFpZXkxdw=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#FF2D2D] hover:text-[#FF2D2D] transition-colors"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram size={14} />
              </a>
            </div>
          </div>

          {/* Sitemap */}
          <div className="md:col-span-3">
            <div className="font-mono-display text-[10px] tracking-[0.3em] text-white/40 uppercase mb-5">
              {lang === "en" ? "Sitemap" : "Карта"}
            </div>
            <ul className="space-y-3 text-sm">
              {[
                ["/", lang === "en" ? "Home" : "Начало"],
                ["/track", t.nav.track],
                ["/fleet", t.nav.fleet],
                ["/pricing", t.nav.pricing],
                ["/experience", t.nav.experience],
                ["/gallery", t.nav.gallery],
                ["/faq", t.nav.faq],
                ["/contact", t.nav.contact],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-white/70 hover:text-[#FF2D2D] transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA + extras */}
          <div className="md:col-span-4">
            <div className="font-mono-display text-[10px] tracking-[0.3em] text-white/40 uppercase mb-5">
              {lang === "en" ? "Reserve" : "Резервирай"}
            </div>
            <button
              onClick={bookNow}
              className="inline-flex items-center gap-3 bg-[#FF2D2D] text-white px-6 py-3.5 text-xs font-bold tracking-[0.16em] uppercase hover:bg-white hover:text-black transition-colors"
              data-testid="footer-book-btn"
            >
              {t.nav.book}
              <ArrowUpRight size={14} />
            </button>
            <a
              href="https://vouchers.racefacer.com/sofiakrasnapoliana"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-3 border border-white/20 px-6 py-3.5 text-xs font-bold tracking-[0.16em] uppercase text-white hover:border-[#FFC700] hover:text-[#FFC700] transition-colors"
              data-testid="footer-vouchers-btn"
            >
              {lang === "en" ? "Buy vouchers" : "Купи ваучер"}
              <ArrowUpRight size={14} />
            </a>

            <div className="mt-8 grain-card p-5">
              <div className="flex items-center justify-between flex-wrap gap-1 mb-2">
                <div className="font-mono-display text-[10px] tracking-[0.25em] uppercase text-[#FFC700]">
                  {lang === "en" ? "Working Hours" : "Работно време"}
                </div>
                <div className="font-mono-display text-[9px] tracking-[0.2em] uppercase text-white/40">
                  {seasonLabel}
                </div>
              </div>
              <div className="space-y-1 text-sm">
                {hours.map((h, i) => (
                  <div key={i} className="flex justify-between text-white/70">
                    <span>{h.day}</span>
                    <span className="font-mono-display text-white">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>



      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/40 font-mono-display tracking-wider uppercase">
          <span>© {year} Sofia Karting · {t.footer.rights}</span>
          <span>{t.footer.tagline}</span>
          <span>{t.footer.built}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;